import { CONSTELLATION_CENTER, CONSTELLATION_DOT_DIAMETER, GOLDEN_RATIO } from './constants';
import { createConstellationFrame } from './model';
import type {
	ConstellationDotColors,
	ConstellationDot,
	ConstellationFrame,
	ConstellationPose,
} from './types';

export type ConstellationSimulationInput = Readonly<{
	active: boolean;
	audioLevel: number;
	dotColors: ConstellationDotColors;
	paused: boolean;
	pose: ConstellationPose;
}>;

type Mutable<T> = { -readonly [Key in keyof T]: T[Key] };

type SimulationDot = Mutable<ConstellationDot> & {
	velocityX: number;
	velocityY: number;
};

export type ConstellationSimulation = {
	dots: SimulationDot[];
	elapsedSeconds: number;
	lastTimestampMs: number;
	smoothedAudioLevel: number;
	twgOpacity: number;
};

const MAX_FRAME_DELTA_SECONDS = 0.032;
const AUDIO_ATTACK_RATE = 14;
const AUDIO_RELEASE_RATE = 4.5;
const BASE_DOT_STIFFNESS = 42;
const DOT_STIFFNESS_INDEX_STEP = 13;
const DOT_STIFFNESS_PATTERN_SIZE = 9;
const DOT_STIFFNESS_VARIATION = 1.1;
const VELOCITY_DAMPING_RATE = 7.2;
const APPEARANCE_RESPONSE_RATE = 10;
const TWG_LAYER_RESPONSE_RATE = 2.6;
const SOFT_WALL_START_RATIO = 1 / Math.sqrt(GOLDEN_RATIO);

const interpolate = (from: number, to: number, progress: number): number =>
	from + (to - from) * progress;

const clampAudioLevel = (audioLevel: number): number => Math.min(1, Math.max(0, audioLevel));
const getAudioGain = (audioLevel: number): number => 0.4 + 1.1 * audioLevel;

const getFrameDeltaSeconds = (previousTimestampMs: number, timestampMs: number): number =>
	Math.min(MAX_FRAME_DELTA_SECONDS, Math.max(0, timestampMs - previousTimestampMs) / 1000);

const updateSmoothedAudioLevel = (
	simulation: ConstellationSimulation,
	audioLevel: number,
	deltaSeconds: number,
): void => {
	const clampedAudioLevel = clampAudioLevel(audioLevel);
	const responseRate =
		clampedAudioLevel > simulation.smoothedAudioLevel ? AUDIO_ATTACK_RATE : AUDIO_RELEASE_RATE;

	simulation.smoothedAudioLevel +=
		(clampedAudioLevel - simulation.smoothedAudioLevel) * Math.min(1, deltaSeconds * responseRate);
};

const createSimulationTargets = (
	simulation: ConstellationSimulation,
	input: ConstellationSimulationInput,
): ConstellationFrame =>
	createConstellationFrame({
		active: input.active,
		audioGain: getAudioGain(simulation.smoothedAudioLevel),
		dotColors: input.dotColors,
		pose: input.pose,
		timeSeconds: simulation.elapsedSeconds,
	});

const getDotStiffness = (index: number): number =>
	BASE_DOT_STIFFNESS +
	((index * DOT_STIFFNESS_INDEX_STEP) % DOT_STIFFNESS_PATTERN_SIZE) * DOT_STIFFNESS_VARIATION;

const updateDotPosition = (
	dot: SimulationDot,
	target: ConstellationDot,
	index: number,
	deltaSeconds: number,
): void => {
	const stiffness = getDotStiffness(index);
	const damping = Math.exp(-VELOCITY_DAMPING_RATE * deltaSeconds);

	// Damped spring motion lets a dot preserve momentum while approaching its target position.
	dot.velocityX = (dot.velocityX + (target.x - dot.x) * stiffness * deltaSeconds) * damping;
	dot.velocityY = (dot.velocityY + (target.y - dot.y) * stiffness * deltaSeconds) * damping;
	dot.x += dot.velocityX * deltaSeconds;
	dot.y += dot.velocityY * deltaSeconds;
};

const updateDotAppearance = (
	dot: SimulationDot,
	target: ConstellationDot,
	deltaSeconds: number,
): void => {
	// Allows dot brightness and scale to change independent of frame rate.
	const progress = 1 - Math.exp(-APPEARANCE_RESPONSE_RATE * deltaSeconds);

	dot.brightness = interpolate(dot.brightness, target.brightness, progress);
	dot.glowStrength = interpolate(dot.glowStrength, target.glowStrength, progress);
	dot.scale = interpolate(dot.scale, target.scale, progress);
	dot.color = target.color;
};

const keepDotInsideSoftWall = (dot: SimulationDot): void => {
	const offsetX = dot.x - CONSTELLATION_CENTER;
	const offsetY = dot.y - CONSTELLATION_CENTER;
	const distanceFromCenter = Math.hypot(offsetX, offsetY);
	const maximumDistance = CONSTELLATION_CENTER - (CONSTELLATION_DOT_DIAMETER / 2) * dot.scale;
	const normalizedDistance = distanceFromCenter / maximumDistance;

	if (normalizedDistance <= SOFT_WALL_START_RATIO) {
		return;
	}

	// Tanh soft wall: compresses overshoot near the edge without visibly hard-clamping motion.
	const compressedDistance =
		maximumDistance *
		(SOFT_WALL_START_RATIO +
			(1 - SOFT_WALL_START_RATIO) *
				Math.tanh((normalizedDistance - SOFT_WALL_START_RATIO) / (1 - SOFT_WALL_START_RATIO)));
	const compression = compressedDistance / distanceFromCenter;

	dot.x = CONSTELLATION_CENTER + offsetX * compression;
	dot.y = CONSTELLATION_CENTER + offsetY * compression;
};

const advanceDot = (
	dot: SimulationDot,
	target: ConstellationDot,
	index: number,
	deltaSeconds: number,
): void => {
	updateDotPosition(dot, target, index, deltaSeconds);
	updateDotAppearance(dot, target, deltaSeconds);
	keepDotInsideSoftWall(dot);
};

const updateTwgOpacity = (
	simulation: ConstellationSimulation,
	pose: ConstellationPose,
	deltaSeconds: number,
): void => {
	const targetOpacity = pose === 'twg' ? 1 : 0;
	const progress = Math.min(1, deltaSeconds * TWG_LAYER_RESPONSE_RATE);

	// Fades the TWG connections in and out as the formation changes pose.
	simulation.twgOpacity += (targetOpacity - simulation.twgOpacity) * progress;
};

export const createConstellationSimulation = (
	input: ConstellationSimulationInput,
	timestampMs: number,
): ConstellationSimulation => ({
	dots: createConstellationFrame({
		active: input.active,
		audioGain: getAudioGain(clampAudioLevel(input.audioLevel)),
		dotColors: input.dotColors,
		pose: input.pose,
		timeSeconds: 0,
	}).map((dot) => ({ ...dot, velocityX: 0, velocityY: 0 })),
	elapsedSeconds: 0,
	lastTimestampMs: timestampMs,
	smoothedAudioLevel: clampAudioLevel(input.audioLevel),
	twgOpacity: input.pose === 'twg' ? 1 : 0,
});

export const advanceConstellationSimulation = (
	simulation: ConstellationSimulation,
	input: ConstellationSimulationInput,
	timestampMs: number,
): ConstellationFrame => {
	const deltaSeconds = getFrameDeltaSeconds(simulation.lastTimestampMs, timestampMs);

	simulation.lastTimestampMs = timestampMs;

	if (input.paused) {
		return simulation.dots;
	}

	simulation.elapsedSeconds += deltaSeconds;
	updateSmoothedAudioLevel(simulation, input.audioLevel, deltaSeconds);
	updateTwgOpacity(simulation, input.pose, deltaSeconds);

	const targets = createSimulationTargets(simulation, input);

	simulation.dots.forEach((dot, index) => {
		advanceDot(dot, targets[index], index, deltaSeconds);
	});

	return simulation.dots;
};
